import { StatusData } from "@fairfleet/geotab";
import { MotorTheme, THEME_ORDER, inferTheme } from "./motorTheme";
import { LiveDataLookups, unitFor } from "@/pages/live-data/models/toLiveDataRow";
import { newestOf } from "@/pages/live-data/utilities/newestReading";
import { sourceIdOf } from "@/utilities/parseGeotabId";

/** One measurement of a vehicle over the period, summarised. */
export interface DiagnosticSummary {
  diagnosticId: string;
  name: string;
  theme: MotorTheme;
  /** Where the reading came from — the engine bus, the telematics unit, an add-on. */
  sourceId: string | null;
  sourceName: string | null;
  unit: string;
  /** How many readings arrived in the period. */
  count: number;
  /** The last reading, as it came, so a non-numeric one is not lost. */
  latest: unknown;
  latestAt: Date;
  /** `null` where no reading was a number: a state or a text reading has no minimum. */
  min: number | null;
  max: number | null;
  average: number | null;
}

/**
 * Summarises a period of readings, one entry per diagnostic.
 *
 * Sorted by group and then by name, so the table reads the same way twice in a row. The readings
 * may come in any order: the newest is found by timestamp, and the statistics do not depend on
 * order, so nothing here sorts the readings.
 *
 * @param statusData - Every reading in the period, in any order.
 * @param lookups - Names, units and sources, the same lookups the live data page uses.
 * @returns One summary per diagnostic that reported.
 */
export function summariseDiagnostics(
  statusData: StatusData[],
  lookups: LiveDataLookups,
): DiagnosticSummary[] {
  const byDiagnostic = new Map<string, StatusData[]>();

  for (const entry of statusData) {
    const diagnosticId = entry.diagnostic?.id;
    if (!diagnosticId) {
      continue;
    }
    const existing = byDiagnostic.get(diagnosticId);
    if (existing) existing.push(entry);
    else byDiagnostic.set(diagnosticId, [entry]);
  }

  const summaries: DiagnosticSummary[] = [];

  for (const [diagnosticId, entries] of byDiagnostic) {
    const newest = newestOf(entries, entry => entry.dateTime) ?? entries[0];
    const numbers = numericStatistics(entries);
    const diagnostic = lookups.diagnostics[diagnosticId];

    summaries.push({
      diagnosticId,
      name: diagnostic?.name ?? diagnosticId,
      theme: inferTheme(diagnosticId, diagnostic),
      sourceId: sourceIdOf(diagnostic?.source) || null,
      sourceName: lookups.sources[diagnosticId] || null,
      unit: unitFor(diagnosticId, lookups),
      count: entries.length,
      latest: newest.data,
      latestAt: new Date(newest.dateTime),
      min: numbers.min,
      max: numbers.max,
      average: numbers.average,
    });
  }

  return summaries.sort(byThemeThenName);
}

function byThemeThenName(first: DiagnosticSummary, second: DiagnosticSummary) {
  const byTheme = THEME_ORDER.indexOf(first.theme) - THEME_ORDER.indexOf(second.theme);
  return byTheme !== 0 ? byTheme : first.name.localeCompare(second.name);
}

/**
 * Smallest, largest and mean of the readings that are numbers, in one pass.
 *
 * Not `Math.min(...values)`: that passes every value as an argument, and a busy vehicle produces
 * enough readings in a day to exceed the call stack limit and throw `RangeError` — which took the
 * page down rather than showing a figure.
 */
function numericStatistics(entries: StatusData[]) {
  let min: number | null = null;
  let max: number | null = null;
  let total = 0;
  let count = 0;

  for (const entry of entries) {
    if (typeof entry.data !== "number") {
      continue;
    }
    const value = entry.data;
    if (!Number.isFinite(value)) {
      continue;
    }
    if (min === null || value < min) min = value;
    if (max === null || value > max) max = value;
    total += value;
    count++;
  }

  return { min, max, average: count > 0 ? total / count : null };
}
